// src/pages/Register.js
import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GlobalContext } from '../context/GlobalContext';
import axios from 'axios';
import { BsEye, BsEyeSlash } from 'react-icons/bs';
import IconsSubmit from '../components/styled/IconsSubmit';
import { API_URL_AUTH } from '../creds';

const Register = () => {
  const navigate = useNavigate();
  const { API_URL, showToast } = useContext(GlobalContext);

  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: '' }); // Clear error on typing
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';

    if (!/^[0-9]{10}$/.test(formData.mobile))
      newErrors.mobile = 'Enter valid 10-digit mobile number';

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = 'Enter a valid email address';

    const strongPass = /^.{8,}$/;
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (!strongPass.test(formData.password)) {
      newErrors.password = 'Password must be at least 8 characters long';
    }

    if (formData.confirmPassword !== formData.password) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    showToast('pending', 'Creating your account...', 'top');

    try {
      const res = await axios.post(`${API_URL_AUTH}/userAuthRegister`, {
        name: formData.name,
        mobile: formData.mobile,
        username: formData.email,
        password: formData.password,
      });

      if (res.data.success) {
        showToast('success', 'Registration successful! Redirecting...', 'top', 6);
        setTimeout(() => navigate('/login'), 2000);
      } else {
        showToast('error', res.data.message || 'Registration failed', 'top');
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Server error. Try again.';
      showToast('failure', msg, 'top', 10);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        {/* Premium Card with Perfect 4-Side Shadow */}
        <div className="bg-white rounded-2xl p-8 md:p-10 border border-gray-200 
                        shadow-[0_0_20px_rgba(0,0,0,0.24)] ring-1 ring-gray-900/5">

          <h1 className="text-3xl font-bold text-center text-gray-900 mb-2">
            Create Account
          </h1>
          <p className="text-center text-gray-600 mb-8">
            Join us today — it's free and fast
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">

            {/* Name */}
            <div className="relative">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`peer w-full px-4 py-4 text-gray-900 bg-transparent border-2 rounded-xl 
                  focus:outline-none transition-all placeholder-transparent
                  ${errors.name ? 'border-red-500' : 'border-gray-300 focus:border-blue-600'}`}
                placeholder="Name"
                required
              />
              <label className={`absolute left-4 -top-2.5 px-2 bg-white text-sm font-medium text-gray-600 
                  transition-all duration-300 
                  peer-placeholder-shown:top-4 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400
                  peer-focus:-top-2.5 peer-focus:text-blue-600 peer-focus:text-sm
                  ${formData.name ? '-top-2.5 text-blue-600 text-sm' : ''}`}
              >
                Full Name
              </label>
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
            </div>

            {/* Mobile */}
            <div className="relative">
              <input
                type="tel"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                className={`peer w-full px-4 py-4 text-gray-900 bg-transparent border-2 rounded-xl 
                  focus:outline-none transition-all placeholder-transparent
                  ${errors.mobile ? 'border-red-500' : 'border-gray-300 focus:border-blue-600'}`}
                placeholder="Mobile Number"
                required
              />
              <label className={`absolute left-4 -top-2.5 px-2 bg-white text-sm font-medium 
                transition-all duration-300 peer-placeholder-shown:top-4 
                peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400
                peer-focus:-top-2.5 peer-focus:text-blue-600 peer-focus:text-sm
                ${formData.mobile ? '-top-2.5 text-blue-600 text-sm' : ''}`}>
                Mobile Number
              </label>
              {errors.mobile && <p className="mt-1 text-sm text-red-600">{errors.mobile}</p>}
            </div>

            {/* Email */}
            <div className="relative">
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`peer w-full px-4 py-4 text-gray-900 bg-transparent border-2 rounded-xl 
                  focus:outline-none transition-all placeholder-transparent
                  ${errors.email ? 'border-red-500' : 'border-gray-300 focus:border-blue-600'}`}
                placeholder="Email"
                required
              />
              <label className={`absolute left-4 -top-2.5 px-2 bg-white text-sm font-medium 
                transition-all duration-300 peer-placeholder-shown:top-4 
                peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400
                peer-focus:-top-2.5 peer-focus:text-blue-600 peer-focus:text-sm
                ${formData.email ? '-top-2.5 text-blue-600 text-sm' : ''}`}>
                Email Address
              </label>
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
            </div>

            {/* Password */}
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`peer w-full px-4 py-4 pr-12 text-gray-900 bg-transparent border-2 rounded-xl 
                  focus:outline-none transition-all placeholder-transparent
                  ${errors.password ? 'border-red-500' : 'border-gray-300 focus:border-blue-600'}`}
                placeholder="Password"
                required
              />
              <label className={`absolute left-4 -top-2.5 px-2 bg-white text-sm font-medium 
                transition-all duration-300 peer-placeholder-shown:top-4 
                peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400
                peer-focus:-top-2.5 peer-focus:text-blue-600 peer-focus:text-sm
                ${formData.password ? '-top-2.5 text-blue-600 text-sm' : ''}`}>
                Password
              </label>
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-4 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <BsEye size={22} /> : <BsEyeSlash size={22} />}
              </button>
              {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
            </div>

            {/* Confirm Password */}
            <div className="relative">
              <input
                type={showConfirm ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`peer w-full px-4 py-4 pr-12 text-gray-900 bg-transparent border-2 rounded-xl 
                  focus:outline-none transition-all placeholder-transparent
                  ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300 focus:border-blue-600'}`}
                placeholder="Confirm Password"
                required
              />
              <label className={`absolute left-4 -top-2.5 px-2 bg-white text-sm font-medium 
                transition-all duration-300 peer-placeholder-shown:top-4 
                peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400
                peer-focus:-top-2.5 peer-focus:text-blue-600 peer-focus:text-sm
                ${formData.confirmPassword ? '-top-2.5 text-blue-600 text-sm' : ''}`}>
                Confirm Password
              </label>
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-4 text-gray-500 hover:text-gray-700"
              >
                {showConfirm ? <BsEye size={22} /> : <BsEyeSlash size={22} />}
              </button>
              {errors.confirmPassword && <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-4 rounded-xl font-semibold text-lg text-white shadow-lg 
                transition-all duration-300 transform
                ${isLoading 
                  ? 'bg-blue-amber-500 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 hover:shadow-2xl hover:-translate-y-0.5'}`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-3">
                  <IconsSubmit />
                  Creating Account...
                </span>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          <p className="mt-8 text-center text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="font-semibold text-blue-600 hover:text-blue-800 hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;